import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {AppButton} from '@components/Core/buttons/AppButton/AppButton';
import AppInput from '../../../Core/inputs/AppInput/AppInput';
import Loading from '../../../shared/Loading/Loading';
import useNotifications from '@hooks/useNotifications';
import useErrorNotification from '@hooks/useErrorNotification';
import {accountUserApi} from '@api/accountUser/api';
import {getErrorMessage} from '@utils/requests/error';
import './verificationCodeStep.less';

interface VerificationCodeStepProps {
    accountId: string;
    userId: string;
    registrationId: string;
    onVerified: () => void;
}

export default function VerificationCodeStep({
    accountId,
    userId,
    registrationId,
    onVerified
}: VerificationCodeStepProps) {
    const intl = useIntl();
    const {showSuccessAlert, showErrorAlert} = useNotifications();
    const [code, setCode] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [verificationError, setVerificationError] = useState<{messageKey: string; parameters: Record<string, string>} | null>(null);

    useErrorNotification(verificationError);

    function handleCodeChange({target}: React.ChangeEvent<HTMLInputElement>) {
        setCode(target.value);
        setVerificationError(null);
    }

    async function handleVerify(e: React.FormEvent) {
        e.preventDefault();

        if (!code.trim()) return;

        setIsVerifying(true);
        setVerificationError(null);

        try {
            await accountUserApi.verifyRegistration({
                accountId,
                userId,
                registrationId,
                data: {secret: parseInt(code, 10)}
            });
            onVerified();
        } catch (err) {
            setVerificationError(getErrorMessage(err));
        } finally {
            setIsVerifying(false);
        }
    }

    async function handleResendCode() {
        setIsResending(true);

        try {
            await accountUserApi.resendRegistration({
                accountId,
                userId,
                registrationId
            });
            showSuccessAlert(intl.formatMessage({id: 'page.admin.inviteUser.codeResent', defaultMessage: ''}));
        } catch (err) {
            showErrorAlert(getErrorMessage(err));
        } finally {
            setIsResending(false);
        }
    }

    const isLoading = isVerifying || isResending;

    return (
        <form className="verification-code-step" onSubmit={handleVerify}>
            <Loading isLoading={isLoading} />
            <h1 className="title">
                {intl.formatMessage({id: 'page.admin.inviteUser.accept', defaultMessage: ''})}
            </h1>
            <p className="description">
                {intl.formatMessage({id: 'page.admin.inviteUser.enterCode', defaultMessage: ''})}
            </p>
            <AppInput
                label={intl.formatMessage({id: 'page.admin.inviteUser.verificationCode', defaultMessage: ''})}
                value={code}
                onChange={handleCodeChange}
                name="verificationCode"
                placeholder="000000"
                required
                dataWdio="verification-code-input"
            />
            <AppButton
                type="submit"
                fullWidth
                disabled={!code.trim() || isLoading}
            >
                {intl.formatMessage({id: 'esl.generic.submit', defaultMessage: ''})}
            </AppButton>
            <button
                type="button"
                className="resend-code-link"
                onClick={handleResendCode}
                disabled={isResending}
            >
                {intl.formatMessage({id: 'page.admin.inviteUser.resendCode', defaultMessage: ''})}
            </button>
        </form>
    );
}
